// ═══════════════════════════════════════════════════════════════
// Session Timeout Warning — shows toast 2 min before expiry
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';

const WARNING_MS = 2 * 60 * 1000; // 2 minutes before expiry
const SESSION_KEY = 'lifeline_session';

export default function SessionTimeout() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const warningShown = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const check = () => {
      try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (!saved) return;
        const session = JSON.parse(saved);
        if (!session.expiresAt) return;

        const remaining = session.expiresAt - Date.now();

        if (remaining <= 0) {
          // Already expired
          useAuthStore.getState().logout();
          notifications.show({
            title: 'Session Expired',
            message: 'Your session has expired. Please log in again.',
            color: 'var(--color-error)',
            autoClose: false,
          });
          return;
        }

        if (remaining <= WARNING_MS && !warningShown.current) {
          warningShown.current = true;
          const mins = Math.ceil(remaining / 60000);
          notifications.show({
            id: 'session-warning',
            title: 'Session Expiring',
            message: `Your session expires in ${mins} minute${mins > 1 ? 's' : ''}. Save your work.`,
            color: '#F86A0B',
            autoClose: false,
            withCloseButton: true,
          });
        }

        // Schedule next check
        if (remaining > WARNING_MS) {
          timerRef.current = setTimeout(check, remaining - WARNING_MS);
        } else if (remaining > 0) {
          timerRef.current = setTimeout(check, remaining);
        }
      } catch { /* ignore parse errors */ }
    };

    check();
    warningShown.current = false;

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated]);

  return null;
}

export { SessionTimeout };
